import React, { useEffect, useState } from 'react';
import CommandPanelImage from 'components/commandpanel/CommandPanelImage';
import {
  GetDisplayMode,
  history,
  isCtrlKeyPressed,
  isNullOrUndefined,
} from 'utils/Global';
import './navigator.scss';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectNavigatorAvailableImagesKeys } from 'redux/selectors/StateSelectors';
import { getNavigatorCommandPanelImageIcons } from 'utils/Navigator/NavigatorUtils';
import {
  useNavigatorImagingSettings,
  usePlateDrawaingIdentifiers,
  useUpdateCompositeImage,
} from './hooks';
import { getNavFilteredScore, getNavigatorImageKeysAsString } from './utils';
import { NavigatorProps } from './NavigatorThumbnail';
import { useAllDropImageParameters, useScoresForWells } from 'redux/hooks/experiment';
import { DropImageParameters, DropUrlParameters } from 'components/Experiment/constants';
import { setWellImagingSettingDetails } from 'redux/reducers/experimentDataReducer';
import { updateDropPath } from 'utils/ExperimentUtils';

const NavigatorImagingSettings = (props: NavigatorProps) => {
  const { setMoveToCanvas, wellNavigationDetails, dropImageParameters } = props;
  const dispatch = useRMDispatch();
  const navAvailableImages = useRMSelector(selectNavigatorAvailableImagesKeys);
  const allWellImagingSettingDetails = useAllDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection, regionName } = dropImageParameters || {};
  const selectDrawingIdentifiers = usePlateDrawaingIdentifiers();
  const plateDrawings = selectDrawingIdentifiers(plateID);
  const disabledImageRegions = plateDrawings?.plateDropRegions;
  const imageBatchId = inspection?.groupedInspections?.find(
    (grp) => grp.plateId === plateID,
  )?.imageBatchId;
  const [navigatorImagingSettings, setNavigatorImagingSettings] = useState([]);
  const updateCompositeImage = useUpdateCompositeImage();
  const displayMode = GetDisplayMode();
  const currentWellScores = useScoresForWells([
    {
      wellNumber: wellNum,
      plateId: plateID,
      dropNumber: dropNumber,
    },
  ]);

  useEffect(() => {
    const compositeKey = getNavigatorImageKeysAsString(
      wellNavigationDetails,
      dropImageParameters,
    );
    if (navAvailableImages.includes(compositeKey)) {
      setNavigatorImagingSettings(useNavigatorImagingSettings(compositeKey));
    }
  }, [navAvailableImages, wellNavigationDetails, dropImageParameters]);

  useEffect(() => {
    updateCompositeImage(wellNavigationDetails, dropImageParameters);
  }, [displayMode]);

  function changeCPId(cpId, ctrlKey) {
    const cpID = parseInt(cpId, 10);
    if (!ctrlKey) {
      updateDropPath(history.location.pathname, [
        { index: DropUrlParameters.CPID, value: cpID },
      ]);
    }
    const navigationDetails: DropImageParameters = {
      ...dropImageParameters,
      cpId: cpID,
    };

    updateCompositeImage(wellNavigationDetails, navigationDetails);

    const selectedNav = allWellImagingSettingDetails.find(
      (w) =>
        w.dropNumber === navigationDetails.dropNumber &&
        w.cpId === cpID &&
        w.inspection === navigationDetails.inspection &&
        w.regionName === navigationDetails.regionName,
    );

    if (
      allWellImagingSettingDetails.length === 1 &&
      !isNullOrUndefined(selectedNav) &&
      ctrlKey
    ) {
      return;
    }
    const filteredNavDetails = allWellImagingSettingDetails.filter(
      (w) =>
        w.dropNumber === navigationDetails.dropNumber &&
        w.cpId !== navigationDetails.cpId &&
        w.inspection.groupedInspections.find((i) => i.imageBatchId === imageBatchId) &&
        w.regionName === navigationDetails.regionName,
    );
    if (!isNullOrUndefined(selectedNav) && ctrlKey) {
      dispatch(setWellImagingSettingDetails([...filteredNavDetails]));
      return;
    }
    if (ctrlKey) {
      if (allWellImagingSettingDetails.length < 4) {
        dispatch(
          setWellImagingSettingDetails([...filteredNavDetails, navigationDetails]),
        );
      }
      setMoveToCanvas(true);
    } else {
      dispatch(setWellImagingSettingDetails([navigationDetails]));
    }
  }

  const isActiveImagingSettings = (_cpId) => {
    if (allWellImagingSettingDetails.length > 1) {
      return allWellImagingSettingDetails.find((details) => details.cpId === _cpId);
    } else {
      return cpId === _cpId;
    }
  };

  return (
    <div id="navigatorimagingsettings-container" className="tabContainer">
      {!isNullOrUndefined(navigatorImagingSettings)
        ? navigatorImagingSettings.map((imagingSettings) => (
            <CommandPanelImage
              image={imagingSettings.image}
              inspectionName={imagingSettings.name}
              imageName={imagingSettings.imageName}
              inspectionDateTime={imagingSettings.dateImaged}
              key={imagingSettings.ImageIndex}
              active={isActiveImagingSettings(imagingSettings.cpId)}
              onClick={(e) => changeCPId(imagingSettings.cpId, isCtrlKeyPressed(e))}
              iconImages={getNavigatorCommandPanelImageIcons(
                plateDrawings,
                disabledImageRegions,
                wellNum,
                dropNumber,
                regionName,
                imagingSettings.cpId,
                imageBatchId,
              )}
              currentWellScores={getNavFilteredScore(
                currentWellScores,
                wellNum,
                dropNumber,
                imageBatchId,
              )}
            />
          ))
        : null}
    </div>
  );
};

export default NavigatorImagingSettings;
